import { useRef, useEffect, useState, useMemo } from 'react'
import * as d3 from 'd3'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme, alpha } from '@mui/material/styles'
import { STACK_ORDER } from './cohorts'
import { HOUR, cumulativeSeries, integrateHours, fmtHours } from './micTime'

const HEIGHT = 318
const MARGIN = { top: 26, right: 30, bottom: 10, left: 64 }
// Bucket silhouette: slightly narrower at the base than at the rim.
const TAPER = 0.84
const CORNER = 10
const WAVE_AMPLITUDE = 2.2
const WAVE_LENGTH = 72
const WAVE_SPEED = 0.0022
// Per-frame approach factor for level/scale changes — closes ~95% of
// any gap in about half a second at 60fps.
const EASE = 0.09
// Slower factor for the first-reveal fill (~1.6s to reach the level).
const FILL_EASE = 0.03
const DROP_FALL_MS = 560
const DROP_STAGGER_MS = 140

const hourLabel = (hour) => {
  if (hour === 0) return '12a'
  if (hour < 12) return `${hour}a`
  if (hour === 12) return '12p'
  return `${hour - 12}p`
}

// Today's per-persona totals plus the summed cumulative timeline (for
// tide marks and the goal-crossing moment).
const dayStats = (day) => {
  const personas = cumulativeSeries(day)
  const totals = (personas[0]?.points || []).map(([ms], index) => [
    ms,
    personas.reduce((sum, persona) => sum + persona.points[index][1], 0),
  ])
  return {
    personas,
    total: personas.reduce((sum, { hours }) => sum + hours, 0),
    totals,
  }
}

// Water level as of each past hour boundary. Tide-mark density is the
// time dimension: a busy hour pushes marks far apart, a dead one
// clusters them.
const buildTideMarks = (totals, dayStart, dayEnd) => {
  const marks = []
  let pointer = 0
  let level = 0
  for (let hour = 1; hour < 24; hour += 1) {
    const ts = dayStart + hour * HOUR
    if (ts > dayEnd) break
    while (pointer < totals.length && totals[pointer][0] <= ts * 1000) {
      level = totals[pointer][1]
      pointer += 1
    }
    marks.push({ hours: level, hour })
  }
  return marks
}

// Runs every animation frame: eases the level, scale, and strata toward
// their targets, redraws the wavy surface clip, and repositions the
// goal line and tide marks against the current (possibly re-scaling)
// capacity. Everything here is cheap attribute writes on ~35 nodes.
const frame = (state, elapsed) => {
  state.capacityCurrent += (state.capacityTarget - state.capacityCurrent) * EASE
  state.personaCurrent = state.personaCurrent.map(
    (value, index) => value + (state.personaTarget[index] - value) * state.ease
  )
  const total = state.personaCurrent.reduce((sum, value) => sum + value, 0)
  const yOf = (hours) =>
    state.bottom - (hours / state.capacityCurrent) * state.plotH

  const surfaceY = yOf(total)
  if (total / state.capacityCurrent < 0.004) {
    state.waveClip.attr('d', 'M 0 0 Z')
  } else {
    const x0 = state.xCenter - state.topHalf
    const x1 = state.xCenter + state.topHalf
    const phase = elapsed * WAVE_SPEED
    let path = ''
    for (let x = x0; x <= x1 + 6; x += 6) {
      const y =
        surfaceY +
        WAVE_AMPLITUDE * Math.sin((x / WAVE_LENGTH) * Math.PI * 2 + phase)
      path += `${path ? 'L' : 'M'} ${x} ${y} `
    }
    path += `L ${x1 + 6} ${state.bottom + 6} L ${x0} ${state.bottom + 6} Z`
    state.waveClip.attr('d', path)
  }

  // Strata are painted bottom persona first; each extends up past its
  // boundary and gets painted over by the one above, so boundaries stay
  // flat while the wave crest is filled by the topmost non-empty color.
  let accumulated = 0
  state.strata.forEach((rect, index) => {
    const yTop = yOf(accumulated + state.personaCurrent[index])
    const yBottom = yOf(accumulated)
    rect
      .attr('y', yTop - (WAVE_AMPLITUDE + 2))
      .attr('height', Math.max(0, yBottom - yTop + WAVE_AMPLITUDE + 2))

    // In-liquid label: each band declares its own running total when
    // it's tall enough to hold one; thin bands rely on hover instead.
    // The persona key joins in once there's room for both lines.
    const label = state.strataLabels[index]
    const bandH = yBottom - yTop
    const opacity = Math.min(1, Math.max(0, (bandH - 15) / 8))
    if (opacity <= 0) {
      label.group.style('opacity', 0)
    } else {
      const center = (yTop + yBottom) / 2
      const showKey = bandH >= 44
      label.group.style('opacity', opacity)
      label.value
        .attr('x', state.xCenter)
        .attr('y', showKey ? center - 4 : center + 6)
        .text(`≈ ${fmtHours(state.personaCurrent[index])}`)
      label.key
        .attr('x', state.xCenter)
        .attr('y', center + 15)
        .style('opacity', showKey ? 1 : 0)
    }

    accumulated += state.personaCurrent[index]
  })

  if (state.typical != null) {
    const goalY = yOf(state.typical)
    const half = state.halfAt(goalY)
    state.goalLine
      .attr('x1', state.xCenter - half)
      .attr('x2', state.xCenter + half + 4)
      .attr('y1', goalY)
      .attr('y2', goalY)
    state.goalLabel.attr('x', state.xCenter + half).attr('y', goalY - 6)
  }

  state.tideEls.forEach(({ line, text }, index) => {
    const mark = state.tideMarks[index]
    if (!mark) {
      line.style('opacity', 0)
      text.style('opacity', 0)
      return
    }
    const y = yOf(mark.hours)
    const wallX = state.xCenter - state.halfAt(y)
    line
      .attr('x1', wallX)
      .attr('x2', wallX + 7)
      .attr('y1', y)
      .attr('y2', y)
      .style('opacity', 0.45)
    text
      .attr('x', wallX - 6)
      .attr('y', y + 3)
      .style('opacity', mark.labeled ? 0.75 : 0)
  })
}

const spawnDrop = (state, persona, deltaHours, order) => {
  const total = state.personaTarget.reduce((sum, value) => sum + value, 0)
  const surfaceY = state.bottom - (total / state.capacityTarget) * state.plotH
  const radius = Math.min(
    7,
    2.5 + (deltaHours / Math.max(state.capacityTarget, 0.001)) * 220
  )
  const x = state.xCenter + (Math.random() - 0.5) * state.topHalf * 1.3

  const drop = state.dropsG
    .append('circle')
    .attr('cx', x)
    .attr('cy', 3)
    .attr('r', radius)
    .attr('fill', persona.color)
    .style('opacity', 0.95)

  drop
    .transition()
    .delay(order * DROP_STAGGER_MS)
    .duration(DROP_FALL_MS)
    .ease(d3.easeCubicIn)
    .attr('cy', surfaceY - 2)
    .on('end', () => {
      drop.remove()
      state.dropsG
        .append('ellipse')
        .attr('cx', x)
        .attr('cy', surfaceY)
        .attr('rx', radius)
        .attr('ry', radius * 0.4)
        .attr('fill', 'none')
        .attr('stroke', persona.color)
        .attr('stroke-width', 1.5)
        .style('opacity', 0.6)
        .transition()
        .duration(420)
        .ease(d3.easeCubicOut)
        .attr('rx', radius * 3.2)
        .attr('ry', radius * 0.9)
        .style('opacity', 0)
        .remove()
    })
}

// The bucket: a stylized, minimal view of today's cumulative mic-on
// time. Water strata are the personas' user-hours; new data falls in as
// colored drops; tide marks on the left wall record the level at each
// hour; the dotted line is a typical full day (trailing week average).
// When the water reaches the goal the bucket autofits — capacity grows,
// the water compresses, and the goal line slides down, staying honest.
const MicTimeBucket = ({ day, week }) => {
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const stateRef = useRef(null)
  // Whether the arrival fill-up has played (survives resize rebuilds, so
  // only the first paint after mount/tab-switch animates from empty).
  const hasFilledRef = useRef(false)
  const [width, setWidth] = useState(0)
  const theme = useTheme()

  const stats = useMemo(
    () => (day && day.series.length > 0 ? dayStats(day) : null),
    [day]
  )

  const weekStats = useMemo(() => {
    if (!week || !day) return null
    const weekHours = integrateHours(week)
    const todayInWeek = integrateHours(week, day.start)
    // Divide by days the metric was actually being written, not calendar
    // days: the pusher zero-fills every cohort each tick, so distinct
    // sample timestamps measure coverage exactly — idle time counts,
    // days before the metric existed don't drag the average down.
    const priorTs = new Set()
    week.series.forEach(({ points }) =>
      points.forEach(([ts]) => {
        if (ts < day.start) priorTs.add(ts)
      })
    )
    const coveredDays = (priorTs.size * week.step_seconds) / (24 * HOUR)
    const calendarDays = (day.start - week.start) / (24 * HOUR)
    // Under one covered day this extrapolates a full day from the slice
    // that has data (biased toward busy hours) — accepted so the goal
    // line still renders while the metric is brand new; the floor stops
    // a few minutes of coverage from exploding the estimate.
    const priorDays = Math.max(Math.min(coveredDays, calendarDays), 0.5)
    return {
      weekHours,
      typicalDay: Math.max(0, weekHours - todayInWeek) / priorDays,
    }
  }, [week, day])

  useEffect(() => {
    const element = containerRef.current
    setWidth(element.offsetWidth)
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0].contentRect.width)
      setWidth((prev) => (Math.abs(prev - next) > 1 ? next : prev))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // Skeleton: bucket walls, clip paths, strata rects, tide/goal/drop
  // chrome, and the animation timer. Rebuilt on width/theme changes;
  // per-frame motion lives in frame().
  useEffect(() => {
    if (!width) return undefined
    const container = containerRef.current
    const uid = Math.round(Math.random() * 1e9)

    const innerW = width - MARGIN.left - MARGIN.right
    const top = MARGIN.top
    const bottom = HEIGHT - MARGIN.bottom
    const plotH = bottom - top
    const xCenter = MARGIN.left + innerW / 2
    const topHalf = innerW / 2
    const bottomHalf = topHalf * TAPER
    const halfAt = (y) =>
      bottomHalf + ((bottom - y) / plotH) * (topHalf - bottomHalf)

    const wallPath = `M ${xCenter - topHalf} ${top}
      L ${xCenter - bottomHalf} ${bottom - CORNER}
      Q ${xCenter - bottomHalf} ${bottom} ${
      xCenter - bottomHalf + CORNER
    } ${bottom}
      L ${xCenter + bottomHalf - CORNER} ${bottom}
      Q ${xCenter + bottomHalf} ${bottom} ${xCenter + bottomHalf} ${
      bottom - CORNER
    }
      L ${xCenter + topHalf} ${top}`

    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', HEIGHT)

    const defs = svg.append('defs')
    defs
      .append('clipPath')
      .attr('id', `bucket-interior-${uid}`)
      .append('path')
      .attr('d', `${wallPath} Z`)
    const waveClip = defs
      .append('clipPath')
      .attr('id', `bucket-wave-${uid}`)
      .append('path')

    const state = {
      xCenter,
      topHalf,
      bottom,
      plotH,
      halfAt,
      waveClip,
      capacityCurrent: 1,
      capacityTarget: 1,
      ease: EASE,
      personaCurrent: STACK_ORDER.map(() => 0),
      personaTarget: STACK_ORDER.map(() => 0),
      typical: null,
      tideMarks: [],
      timeouts: [],
      fresh: true,
    }

    const tooltip = tooltipRef.current
    const moveTip = (event) => {
      // Pointer coords come back in visual (post-transform) pixels;
      // divide by the wallboard scale (visual width over layout width)
      // so the tooltip lands in local coordinates.
      const scale =
        container.getBoundingClientRect().width / container.offsetWidth || 1
      const [x, y] = d3.pointer(event, container)
      tooltip.style.left = `${x / scale + 14}px`
      tooltip.style.top = `${y / scale - 12}px`
    }

    const waterG = svg
      .append('g')
      .attr('clip-path', `url(#bucket-interior-${uid})`)
    const strataG = waterG
      .append('g')
      .attr('clip-path', `url(#bucket-wave-${uid})`)
    state.strata = STACK_ORDER.map((persona, index) =>
      strataG
        .append('rect')
        .attr('x', xCenter - topHalf)
        .attr('width', topHalf * 2)
        .attr('fill', persona.color)
        .attr('fill-opacity', 0.9)
        .on('mouseenter', () => {
          tooltip.innerHTML = `<b>${persona.key}</b> — ≈ ${fmtHours(
            state.personaTarget[index]
          )} today`
          tooltip.style.opacity = 1
        })
        .on('mousemove', moveTip)
        .on('mouseleave', () => {
          tooltip.style.opacity = 0
        })
    )
    // Labels sit on the water (bucket-clipped, above the strata) and are
    // positioned every frame so they ride the level as it rises. Styled
    // like the On-right-now bubbles: bold value, persona key beneath.
    state.strataLabels = STACK_ORDER.map((persona) => {
      const group = waterG
        .append('g')
        .style('pointer-events', 'none')
        .style('opacity', 0)
      return {
        group,
        value: group
          .append('text')
          .attr('text-anchor', 'middle')
          .style('fill', 'white')
          .style('font-size', '16px')
          .style('font-weight', 700),
        key: group
          .append('text')
          .attr('text-anchor', 'middle')
          .style('fill', 'white')
          .style('font-size', '11px')
          .style('font-weight', 600)
          .style('letter-spacing', '1px')
          .text(persona.key),
      }
    })

    svg
      .append('path')
      .attr('d', wallPath)
      .attr('fill', 'none')
      .attr('stroke', alpha(theme.palette.text.secondary, 0.7))
      .attr('stroke-width', 2)
      .attr('stroke-linejoin', 'round')

    const tideColor = theme.palette.text.secondary
    state.tideEls = d3.range(23).map(() => ({
      line: svg
        .append('line')
        .attr('stroke', tideColor)
        .attr('stroke-width', 1)
        .style('opacity', 0),
      text: svg
        .append('text')
        .attr('text-anchor', 'end')
        .style('font-size', '9px')
        .style('fill', tideColor)
        .style('opacity', 0),
    }))

    state.goalLine = svg
      .append('line')
      .attr('stroke', theme.palette.text.primary)
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '5 4')
      .style('opacity', 0)
    state.goalLabel = svg
      .append('text')
      .attr('text-anchor', 'end')
      .style('font-size', '10px')
      .style('fill', theme.palette.text.secondary)

    state.dropsG = svg.append('g')

    state.timer = d3.timer((elapsed) => frame(state, elapsed))
    stateRef.current = state

    return () => {
      state.timer.stop()
      state.timeouts.forEach(clearTimeout)
      container.innerHTML = ''
      stateRef.current = null
    }
  }, [width, theme])

  useEffect(() => {
    const state = stateRef.current
    if (!state || !stats || !day) return

    const typical = weekStats ? weekStats.typicalDay : null
    state.typical = typical > 0 ? typical : null
    // Goal at 80% of the bucket; a big day outgrows that and the
    // capacity autofits so the water always has 15% headroom.
    state.capacityTarget = Math.max(
      state.typical != null ? state.typical / 0.8 : 0,
      stats.total * 1.15,
      0.05
    )

    state.tideMarks = buildTideMarks(stats.totals, day.start, day.end)
    let lastLabelY = Infinity
    state.tideMarks.forEach((mark) => {
      const y = state.bottom - (mark.hours / state.capacityTarget) * state.plotH
      mark.labeled = lastLabelY - y >= 12
      if (mark.labeled) lastLabelY = y
    })
    state.tideEls.forEach((els, index) => {
      const mark = state.tideMarks[index]
      if (mark) els.text.text(hourLabel(mark.hour))
    })

    if (state.typical != null) {
      const crossed = stats.totals.find(([, hours]) => hours >= state.typical)
      state.goalLabel.text(
        crossed
          ? `typical day · reached ${dayjs(crossed[0]).format('h:mm a')}`
          : 'typical day'
      )
      state.goalLine.style('opacity', crossed ? 0.9 : 0.6)
    } else {
      state.goalLine.style('opacity', 0)
      state.goalLabel.text('')
    }

    const personaTarget = stats.personas.map(({ hours }) => hours)
    if (state.fresh) {
      state.capacityCurrent = state.capacityTarget
      state.personaTarget = [...personaTarget]
      if (hasFilledRef.current) {
        // Post-resize rebuilds: no theatrics, just the current state.
        state.personaCurrent = [...personaTarget]
      } else {
        // Arrival: the water rises from an empty bucket to the level of
        // the day so far, easing out as it settles.
        hasFilledRef.current = true
        state.personaCurrent = STACK_ORDER.map(() => 0)
        state.ease = FILL_EASE
        state.timeouts.push(
          setTimeout(() => {
            state.ease = EASE
          }, 2000)
        )
      }
      state.fresh = false
      return
    }

    let order = 0
    personaTarget.forEach((target, index) => {
      const delta = target - state.personaTarget[index]
      if (delta > 1e-9) {
        spawnDrop(state, STACK_ORDER[index], delta, order)
        order += 1
      }
    })
    if (order === 0) {
      // Shrinks happen when a server step-size change re-integrates the
      // day; no drops for those, just a quiet settle.
      state.personaTarget = personaTarget
    } else {
      // The water rises when the drops land, not before.
      const timeout = setTimeout(() => {
        state.personaTarget = personaTarget
      }, DROP_FALL_MS + order * DROP_STAGGER_MS)
      state.timeouts.push(timeout)
    }
  }, [stats, weekStats, day, width, theme])

  const totalHours = stats ? stats.total : 0

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          columnGap: 2,
          mb: 0.5,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Mic-on time
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h5" fontWeight={800} lineHeight={1}>
            ≈ {fmtHours(totalHours)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            user-hours today
          </Typography>
        </Box>
      </Box>
      {weekStats && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block' }}
        >
          {weekStats.typicalDay != null
            ? `typical day ≈ ${fmtHours(weekStats.typicalDay)} · `
            : ''}
          last 7 days ≈ {fmtHours(weekStats.weekHours)}
        </Typography>
      )}
      <Box sx={{ position: 'relative' }}>
        <Box ref={containerRef} sx={{ width: '100%', height: HEIGHT }} />
        <Box
          ref={tooltipRef}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.15s',
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            px: 1.25,
            py: 0.5,
            borderRadius: 1,
            fontSize: 12,
            whiteSpace: 'nowrap',
            zIndex: 1,
          }}
        />
      </Box>
    </>
  )
}

export default MicTimeBucket
